import { Fragment, useEffect, memo, useMemo, useCallback, useState } from "react";
import { Divider, MaskedInput, InputNumber, NumberInput } from "rsuite";
import { Button, Card, Col, Container, Modal, Row, Stack, FormSelect } from "react-bootstrap";
import { ArrowRight, CheckLg, ChevronDoubleDown, ChevronDown, LockFill, ChevronUp, PlusLg, Trash, UnlockFill, XLg } from "react-bootstrap-icons";
import { axiosInstance } from "../../auth/Auth";
import { useEditableList } from "../HelperHooks";
import { PLAP_CONDITION_OPERATORS, PLAP_CONDITION_TREND_OPTIONS, PLAP_CONDITION_TREND_VALUES, PLAP_CONDITION_VARIABLES } from "../constants";
import { getSongScoreColorCss } from "../HelperFunctions";
import LoadingIndicator from "../LoadingIndicator";
import RATooltip from "../RATooltip";

const ruleCodeMap = [
    ["move_up_code", "up"],
    ["move_down_code", "down"],
    ["move_out_code", "out"],
];

export default function PlaylistAutopilotSettingsRulesModal({ categoryId, categories, onClose }) {
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(false);

    const categoryMap = useMemo(() => new Map(categories.map(x => [x.category_id, x])), [categories]);
    const categoryCodeMap = useMemo(() => new Map(categories.map(x => [x.code, x])), [categories]);

    useEffect(() => {
        if (!categoryId) {
            setCategory(null);
            return;
        }

        if (category && category.category_id === categoryId) return;

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const { data } = await axiosInstance.get(`playlist_autopilot/categories/categories/${categoryId}`);
                if (!cancelled) setCategory(data);
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to load category", err);
                    setCategory(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => (cancelled = true);
    }, [category, categoryId]);

    const initialRules = useMemo(() => {
        if (!category) return [];

        const currentRules = category["rules"] || [];
        let newRules = [];

        for (const [index, [catKey, ruleType]] of ruleCodeMap.entries()) {
            if (category[catKey] == null) continue;
            const existingRule = currentRules.find(x => x.rule_type === ruleType);
            if (existingRule) newRules.push(existingRule);
            else newRules.push({ rule_id: -(index + 1), rule_type: ruleType, category_id: category.category_id, name: null, groups: [], status: "ADD" });
        }
        return newRules;
    }, [category]);

    const isInvalid = item => !(item.rule_type && item.category_id);

    const handleCreate = async item => {
        if (isInvalid(item)) throw new Error("Invalid payload");
        const newId = (await axiosInstance.post("playlist_autopilot/categories/rules", item)).data;
        return { ...item, rule_id: newId };
    };

    const { items: rulesCopy, handlers: ruleHandlers } = useEditableList({
        initialItems: initialRules,
        idKey: "rule_id",
        itemName: "rule",
        createTempItem: (_, props) => ({ status: "ADD", ...props }),
        createItem: handleCreate,
        deleteItem: async ({ rule_id }) => await axiosInstance.delete(`playlist_autopilot/categories/rules/${rule_id}`),
        onDeleted: () => setCategory(null), // To re-fetch categories to show placeholders
    });

    return (
        <Modal show={!!categoryId} onHide={onClose} size="lg" centered enforceFocus={false}>
            <style>{`
                .playlist-autopilot-rule-tooltip > .tooltiptext {
                    top: calc(100% + 0.25rem);
                    right: 0;
                    left: auto;
                }
            `}</style>
            <Modal.Header closeButton>
                <Modal.Title>
                    <span className="fw-bold text-ra-yellow">
                        [{category?.code ?? "-"}] {category?.name ?? "--"}
                    </span>{" "}
                    - Rotation Rules
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && <LoadingIndicator width={75} />}
                <Stack gap={3}>
                    {rulesCopy.map(r => (
                        <Rule key={r.rule_id} rule={r} categoryMap={categoryMap} categoryCodeMap={categoryCodeMap} ruleHandlers={ruleHandlers} />
                    ))}
                </Stack>
            </Modal.Body>
        </Modal>
    );
}

const RuleCondition = ({ condition, handlers }) => {
    const hasChanged = useMemo(() => handlers.hasChanged(condition), [condition, handlers]);
    const NonNegotiableIcon = condition.non_negotiable ? LockFill : UnlockFill;
    const toggleNonNegotiable = () => handlers.change(condition, "non_negotiable", !condition.non_negotiable);
    const handleNonNegotiableKeyDown = event => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleNonNegotiable();
        }
    };

    return (
        <Col xs="12" className=" border-reset">
            <Row className=" gx-2 align-items-center flex-nowrap" style={{ height: "3rem" }}>
                <Col xs="5">
                    <FormSelect
                        size="md"
                        value={condition.variable || ""}
                        onChange={e => {
                            handlers.change(condition, "variable", e.target.value);
                            handlers.change(condition, "value", null);
                        }}>
                        <option value="" disabled hidden>
                            Select variable
                        </option>
                        {Object.entries(PLAP_CONDITION_VARIABLES).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </FormSelect>
                </Col>
                {condition.variable !== "score_trend" && condition.variable !== "burn_trend" && (
                    <Col>
                        <FormSelect
                            size="md"
                            value={condition.operator || ""}
                            disabled={!condition.variable}
                            onChange={e => handlers.change(condition, "operator", e.target.value)}>
                            <option value="" disabled hidden>
                                Select operator
                            </option>
                            {Object.entries(PLAP_CONDITION_OPERATORS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </FormSelect>
                    </Col>
                )}
                {!condition.variable && (
                    <Col xs="auto">
                        <Col xs="auto">
                            <NumberInput size="md" placeholder="0" disabled style={{ width: "4rem" }} />
                        </Col>
                    </Col>
                )}
                {condition.variable === "score" && <ScoreControl condition={condition} onChange={handlers.change} />}
                {(condition.variable === "score_trend" || condition.variable === "burn_trend") && (
                    <TrendControl condition={condition} onChange={handlers.change} />
                )}
                {condition.variable === "spins" && <NumberControl condition={condition} onChange={handlers.change} chars={4} />}
                {condition.variable === "burn" && <ScoreControl condition={condition} onChange={handlers.change} inverted />}
                {condition.variable === "relative_age" && <NumberControl condition={condition} onChange={handlers.change} chars={3} />}
                {condition.variable === "release_year" && <ReleaseYearControl condition={condition} onChange={handlers.change} />}
                {condition.variable === "exposure" && <ScoreControl condition={condition} onChange={handlers.change} noScale />}
                <Col xs="auto">
                    <RATooltip
                        className="playlist-autopilot-rule-tooltip"
                        tooltipElement={
                            <NonNegotiableIcon
                                role="button"
                                tabIndex={0}
                                aria-label={condition.non_negotiable ? "Make rule breakable" : "Make rule non-breakable"}
                                onClick={toggleNonNegotiable}
                                onKeyDown={handleNonNegotiableKeyDown}
                                style={{ width: 24, height: 24, cursor: "pointer" }}
                            />
                        }>
                        <div>
                            <h5>Breakable / Non-Breakable</h5>
                            <span>
                                Songs are moved to categories based on all defined rules. If a category is not filled, breakable{" "}
                                <UnlockFill size={20} style={{ verticalAlign: "top" }} /> rules may be ignored, but non-breakable{" "}
                                <LockFill size={20} style={{ verticalAlign: "top" }} /> rules must always be followed.
                            </span>
                        </div>
                    </RATooltip>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success-contained"
                        className="px-1"
                        title="Save"
                        onClick={async () => await handlers.save(condition)}
                        disabled={!hasChanged || !(condition.operator && condition.value != null && condition.variable)}>
                        <CheckLg size={24} />
                    </Button>
                    {hasChanged ? (
                        <Button variant="danger-contained" className="px-1" title="Discard" onClick={() => handlers.discard(condition)}>
                            <XLg size={24} />
                        </Button>
                    ) : (
                        <Button variant="danger-contained" className="px-1" title="Remove" onClick={async () => await handlers.remove(condition)}>
                            <Trash size={24} />
                        </Button>
                    )}
                </Col>
            </Row>
        </Col>
    );
};

const RuleGroup = ({ group, groupsHandlers }) => {
    const isInvalid = useCallback(item => !(item.operator && item.value != null && item.variable), []);

    const handleCreate = useCallback(
        async item => {
            if (isInvalid(item)) throw new Error("Invalid payload");
            const newId = (await axiosInstance.post("playlist_autopilot/categories/conditions", item)).data;
            return { ...item, condition_id: newId };
        },
        [isInvalid],
    );

    const handleUpdate = useCallback(
        async item => {
            if (isInvalid(item)) throw new Error("Invalid payload");
            await axiosInstance.put(`playlist_autopilot/categories/conditions/${item.condition_id}`, item);
        },
        [isInvalid],
    );

    const { items: conditionsCopy, handlers: conditionHandlers } = useEditableList({
        initialItems: group["conditions"],
        idKey: "condition_id",
        itemName: "rule condition",
        createTempItem: (curr, props) => ({ condition_id: -(curr.length + 1), group_id: group.group_id, status: "ADD", ...props }),
        createItem: handleCreate,
        updateItem: handleUpdate,
        deleteItem: async ({ condition_id }) => await axiosInstance.delete(`playlist_autopilot/categories/conditions/${condition_id}`),
    });

    return (
        <Container>
            <Row>
                <Col>
                    <Row className="border border-1 border-ra-dark-blue rounded-3 py-2">
                        {conditionsCopy.map(x => (
                            <Fragment key={x.condition_id}>
                                <RuleCondition condition={x} handlers={conditionHandlers} />
                                <Divider label="AND" className="fs-8" />
                            </Fragment>
                        ))}
                        <Col xs="12" className="text-center">
                            <Button size="sm" variant="ra-yellow" className="icon-btn" onClick={() => conditionHandlers.add()}>
                                <PlusLg /> Add Condition
                            </Button>
                        </Col>
                    </Row>
                </Col>
                <Col xs="1">
                    <Button variant="outline-danger" className="h-100" onClick={async () => await groupsHandlers.remove(group)}>
                        <Trash size={24} />
                    </Button>
                </Col>
            </Row>
        </Container>
    );
};

const Rule = ({ rule, categoryMap, categoryCodeMap, ruleHandlers }) => {
    const isNew = rule.status === "ADD";

    const isInvalid = item => !item.rule_id;

    const handleCreate = async item => {
        if (isInvalid(item)) throw new Error("Invalid payload");
        const newId = (await axiosInstance.post("playlist_autopilot/categories/groups", item)).data;
        return { ...item, group_id: newId };
    };

    const { items: groupsCopy, handlers: groupsHandlers } = useEditableList({
        initialItems: rule["groups"],
        idKey: "group_id",
        itemName: "rule group",
        createTempItem: (_, props) => ({ ...props }),
        createItem: handleCreate,
        deleteItem: async ({ group_id }) => await axiosInstance.delete(`playlist_autopilot/categories/groups/${group_id}`),
    });

    const onNewGroup = useCallback(async () => {
        const newGroup = { group_id: -(groupsCopy.length + 1), rule_id: rule.rule_id, conditions: [], status: "ADD" };
        groupsHandlers.add(newGroup);
        await groupsHandlers.save(newGroup);
    }, [groupsCopy.length, rule.rule_id, groupsHandlers]);

    return (
        <Card className="border-dark-blue ">
            <Card.Header className={`p-2 ${isNew ? "opacity-50" : ""}`}>
                <Row direction="horizontal" gap={2} className="fs-5 justify-content-center">
                    <Col xs={{ offset: 1 }}>
                        <Stack direction="horizontal" gap={2} className="fs-5 justify-content-center">
                            <span className=" text-uppercase d-inline-flex align-items-center gap-1">
                                {rule.rule_type}
                                {rule.rule_type === "up" ? (
                                    <ChevronUp className="text-success" size={20} />
                                ) : rule.rule_type === "down" ? (
                                    <ChevronDown className="text-danger" size={20} />
                                ) : (
                                    <ChevronDoubleDown className="text-danger" size={20} />
                                )}
                            </span>
                            <ArrowRight size={20} />
                            <span className="fw-bold">[{categoryMap.get(rule.category_id)[`move_${rule.rule_type}_code`]}]</span>
                            <span>{categoryCodeMap.get(categoryMap.get(rule.category_id)[`move_${rule.rule_type}_code`])?.name}</span>
                        </Stack>
                    </Col>
                    <Col xs="1">
                        {!isNew && (
                            <Button
                                variant="danger"
                                size="md"
                                className="icon-btn ms-auto"
                                title="Remove Rule"
                                onClick={async () => await ruleHandlers.remove(rule)}>
                                <XLg />
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {groupsCopy.map(g => (
                    <Fragment key={g.group_id}>
                        <RuleGroup group={g} groupsHandlers={groupsHandlers} />
                        <Divider label="OR" className="fs-8" spacing={"md"} />
                    </Fragment>
                ))}
                <Row>
                    <Col className="d-flex flex-column">
                        {isNew ? (
                            <Button
                                variant="ra-dark-blue-outline"
                                className="icon-btn justify-content-center"
                                onClick={async () => await ruleHandlers.save(rule)}>
                                <PlusLg />
                                Create Rule
                            </Button>
                        ) : (
                            <Button variant="ra-dark-blue-outline" className="icon-btn justify-content-center" onClick={onNewGroup}>
                                <PlusLg />
                                Add Group
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};
const ScoreControl = memo(({ condition, onChange, inverted = false, noScale = false }) => (
    <Col xs="auto">
        <NumberInput
            size="lg"
            className={`score-input ${!noScale && getSongScoreColorCss(inverted && condition.value ? 10.01 - condition.value : condition.value, true)}`}
            placeholder={0}
            value={condition.value}
            step={0.1}
            min={0}
            max={10}
            onChange={value => onChange(condition, "value", value)}
            onBlur={() => {
                if (condition.value == null) return;
                const clamped = Math.min(10, Math.max(0, condition.value));
                onChange(condition, "value", Math.round(clamped * 10) / 10);
            }}
        />
    </Col>
));

const NumberControl = memo(({ condition, onChange, chars = 3 }) => (
    <Col xs="auto">
        <NumberInput
            size="lg"
            value={condition.value}
            min={0}
            onChange={value => onChange(condition, "value", value)}
            onBlur={() => condition.value && onChange(condition, "value", Math.round(condition.value))}
            style={{ width: 5 + chars + "ch" }}
        />
    </Col>
));

const ReleaseYearControl = memo(({ condition, onChange }) => (
    <Col xs="auto">
        <MaskedInput
            size="lg"
            mask={[/\d/, /\d/, /\d/, /\d/]}
            showMask
            keepCharPositions
            placeholderChar="-"
            value={condition.value}
            min={0}
            onChange={value => onChange(condition, "value", value)}
            style={{ width: 60 }}
        />
    </Col>
));

const TrendControl = memo(({ condition, onChange }) => {
    const activeTrend = useMemo(
        () =>
            Object.keys(PLAP_CONDITION_TREND_VALUES).find(
                key => PLAP_CONDITION_TREND_VALUES[key].operator === condition.operator && PLAP_CONDITION_TREND_VALUES[key].value === condition.value,
            ) ?? "",
        [condition.operator, condition.value],
    );

    const handleTrendChange = useCallback(
        trendKey => {
            const trendValue = PLAP_CONDITION_TREND_VALUES[trendKey];
            onChange(condition, "operator", trendValue.operator);
            onChange(condition, "value", trendValue.value);
        },
        [condition, onChange],
    );

    return (
        <Col>
            <FormSelect size="md" value={activeTrend} onChange={e => handleTrendChange(e.target.value)}>
                <option value="" disabled hidden>
                    Select
                </option>
                {Object.entries(PLAP_CONDITION_TREND_OPTIONS).map(([value, label]) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </FormSelect>
        </Col>
    );
});
